import { createApp, h, markRaw } from "vue";
import Toast, { TYPE } from "vue-toastification";
import vSelect from "vue-select";
import FloatingVue from "floating-vue";
import VueTelInput from "vue-tel-input";
import VueGtag from "vue-gtag";
import { createPinia } from "pinia";

import { Crisp } from "crisp-sdk-web";
import moment from "moment";
import introJs from "intro.js";
import "moment/dist/locale/fr";
import mitt from "mitt";
import "./vee-validate";
import { i18n } from "./i18n";

import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '@vueup/vue-quill/dist/vue-quill.bubble.css';

import { BrowserTracing } from "@sentry/tracing";
import * as Sentry from "@sentry/vue";

import { safeHtml } from './directives/safeHtml';
import App from "./App.vue";
import appRouter from "./router/appRouter";
import gameRouter from "./router/gameRouter";
import experienceRouter from "./router/experienceRouter";

// Impport style sheets
import "vue-select/dist/vue-select.css";
import "floating-vue/dist/style.css";
import "intro.js/minified/introjs.min.css";
import "vue-tel-input/vue-tel-input.css";
import "./assets/style/main.scss";

import { VueQueryPlugin } from '@tanstack/vue-query';
import { queryClient } from '@/queries/queryClient';

// Define the router
const router = () => {
  const host = window.location.host;

  if (host.includes("game")) {
    return gameRouter;
  } else if (host.includes("experience")) {
    return experienceRouter;
  } else {
    return appRouter;
  }
};

// Create instances
const app = createApp(App);
const pinia = createPinia();
const momentInstance = moment;
const emitter = mitt();
const crispInstance = Crisp;
const { t } = i18n.global;

app.directive('safe-html', safeHtml);

app.use(VueQueryPlugin, {
  queryClient
});

// Config some libraries
// |_ VSelect
vSelect.props.components.default = () => ({
  Deselect: {
    render: () => h("i", { class: "bi bi-x" }, ""),
  },
  OpenIndicator: {
    render: () => h("i", { class: "bi bi-chevron-down" }, ""),
  },
});
// |_ Deps inside pinia
pinia.use(({ store }) => {
  store.$moment = markRaw(momentInstance);
  store.$router = markRaw(router());
  store.$emitter = markRaw(emitter);
  store.$introJs = markRaw(
    introJs().setOptions({
      nextLabel: t('other.next'),
      prevLabel: t('other.previous'),
    })
  );
  store.$crisp = markRaw(crispInstance);
});
// |_ Tooltips
FloatingVue.options.themes.tooltip.distance = 10;
// |_ Moment
momentInstance.locale("fr");
// |_ Crisp
crispInstance.configure("d7ba28c0-d105-41c0-8d97-0678de81eaf8", {
  autoload: import.meta.env.VITE_ENV === "production",
});
// |_ Sentry
Sentry.init({
  app,
  environment: import.meta.env.VITE_ENV,
  dsn: "https://90f1321137644269ad3f7f08a29b61b5@o4504575862833152.ingest.sentry.io/4504791148855296",
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router()),
      tracePropagationTargets: ["yuzu-4255c.web.app", /^\//],
    }),
  ],
  tracesSampleRate: 1.0,
  enabled: import.meta.env.PROD,
});

// Use some libraries
app.use(pinia);
app.use(router());
app.use(i18n);
app.use(FloatingVue);
app.provide("moment", momentInstance);
app.provide("emitter", emitter);
app.provide("crisp", crispInstance);
app.use(Toast, {
  transition: "Vue-Toastification__fade",
  timeout: 5000,
  icon: {
    iconTag: "i",
    iconClass: "bi bi-info-circle",
  },
  toastDefaults: {
    [TYPE.ERROR]: {
      icon: {
        iconClass: "bi bi-x-circle",
      },
    },
    [TYPE.SUCCESS]: {
      icon: {
        iconClass: "bi-check2-circle",
      },
    },
    [TYPE.WARNING]: {
      icon: {
        iconClass: "bi bi-exclamation-circle",
      },
    },
  },
});
app.use(VueTelInput, {
  defaultCountry: "fr",
  preferredCountries: ["fr", "be", "lu", "ch"],
  dropdownOptions: {
    showSearchBox: true,
    showFlags: true,
  },
  inputOptions: {
    showDialCode: true,
    placeholder: t('other.form.phone.placeholder'),
  },
});
app.use(
  VueGtag,
  {
    enabled: import.meta.env.VITE_ENV === "production",
    pageTrackerTemplate(to) {
      return {
        page_title: to.meta.document_title,
        page_path: to.path,
      };
    },
    config: {
      id: "G-DCG1Y8HF5J",
      params: {
        send_page_view: true,
      },
    },
  },
  appRouter
);

app.component('QuillEditor', QuillEditor);

// Register some components
const components = import.meta.glob("./components/global/*.vue", { eager: true });

Object.entries(components).forEach(([path, definition]) => {
  const componentName = path
    .split("/")
    .pop()
    .replace(/\.\w+$/, "");

  app.component(componentName, definition.default);
});

app.component("v-select", vSelect);

// Mont the app
app.mount("#app");
